import { useMemo } from 'react';
import {
  FeConstants,
  StageColors,
  stagesColors,
  stagesColorsMap,
  wonAndLostColors,
} from 'constants/FeConstants';
import { Constants } from 'crono-fe-common/constants/constants';
import { IntegrationsOnboarding } from 'crono-fe-common/types/DTO/integrationsOnboarding';
import { Account, AccountStatus } from 'crono-fe-common/types/account';
import { TaskTodo } from 'crono-fe-common/types/cronoTaskTodo';
import { EmailMessage } from 'crono-fe-common/types/emailMessage';
import IntegrationType from 'crono-fe-common/types/enums/integrationType';
import { doesCrmSupportLeads } from 'crono-fe-common/utils/crm-utils';
import Role from 'crono-fe-common/types/enums/role';
import { TaskTodoType } from 'crono-fe-common/types/enums/taskTodoType';
import {
  ExternalProperty,
  ExternalPropertyShort,
  TableType,
} from 'crono-fe-common/types/externalProperty';
import { Prospect } from 'crono-fe-common/types/prospect';
import { TemplateVariables } from 'crono-fe-common/types/template';
import { getFullName, User } from 'crono-fe-common/types/user';
import {
  getExternalPropertyLabels,
  htmlToText,
  sortStagesByDisplayOrder,
  TEMPLATE_VARIABLE_MISSING,
  transformToValidUrl,
  getError,
} from 'crono-fe-common/utils';
import { Opportunity } from 'crono-fe-common/types/DTO/opportunityViewResponseDTO';
import { Pipeline } from 'crono-fe-common/types/pipeline';
import PATH from 'crono-fe-common/constants/path';
import { CronoErrorCode } from 'crono-fe-common/types/enums/cronoErrorCode';
import SubscriptionPlanType from 'crono-fe-common/types/enums/subscriptionPlanType';
import { StatusComponentOptions } from 'crono-fe-common/types/StatusOptions';
import useGetExternalPropertyStatus from 'hooks/services/externalProperty/useGetExternalPropertyStatus';
import {
  DefaultCompanyStatus,
  DefaultContactStatus,
  DefaultDefaultCompanyStatus,
  DefaultDefaultContactStatus,
} from 'crono-fe-common/types/model';
import { StrategyStep } from 'crono-fe-common/types/strategyStep';
import { pluralize } from 'utils/pluralize';
import { TaskTodoSubtype } from 'crono-fe-common/types/enums/taskTodoSubtype';
import { Options, Plan } from 'pages/signup/types';
import TemplateType from 'crono-fe-common/types/enums/templateType';
import { SubscriptionStripe } from 'crono-fe-common/types/subscriptionStripe';
import { CronoExtensionStatus } from 'hooks/services/crono-extension/useGetExtensionStatus';
import { Response } from 'crono-fe-common/types/response';
import { RewriteError } from 'crono-fe-common/types/enums/rewriteError';
import { CronoTaskTodoWarning } from 'crono-fe-common/types/enums/cronoTaskTodoWarning';
import { SwitchToTask } from 'crono-fe-common/types/enums/switchToConditionalStep';
import { Mailbox } from 'crono-fe-common/types/mailbox';
import { ConversationType } from 'crono-fe-common/types/ConversationType';
import { IVariable } from 'pages/templates/model';
import { CallFeedback, CallSentiment } from 'crono-fe-common/types/logCall';
import { BillingPlan } from 'pages/signup/model';
import { ProspectSearchStrategyResponse } from 'crono-fe-common/types/prospectSearchStrategy';
import { ProspectLinkedin } from 'crono-fe-common/types/prospectLinkedin';
import CronoError from 'crono-fe-common/types/error';
import { AuthErrorCode } from 'crono-fe-common/types/enums/authErrorCode';

export const FIXED_VARIABLE_LENGTH = 20;

export function IsHomeRoute(route: string): boolean {
  return route === PATH.HOME;
}

export function openLinkedinAccount(account?: Account | null) {
  if (account?.linkedin && transformToValidUrl(account?.linkedin) != null) {
    window.open(transformToValidUrl(account.linkedin)!, '_blank');
  } else {
    window.open(Constants.linkedinBaseUrl, '_blank');
  }
}

export function openLinkedinProspect(prospect?: Prospect | null) {
  if (prospect?.linkedin && transformToValidUrl(prospect?.linkedin) != null) {
    window.open(transformToValidUrl(prospect.linkedin)!, '_blank');
  } else {
    window.open(Constants.linkedinBaseUrl, '_blank');
  }
}

export const getExternalPropertyInputKey = (
  propertyId: number,
  externalValues:
    | { externalPropertyId: number; value: string }[]
    | null
    | undefined,
): string => {
  const hasValue =
    externalValues?.some((v) => v.externalPropertyId === propertyId) ?? false;
  return `${propertyId}-${hasValue}`;
};

export const createExternalPropertyVariable = (
  externalProperty: ExternalProperty,
): IVariable => {
  return {
    variable: externalProperty.templateVariable ?? '',
    text: externalProperty.publicName ?? externalProperty.externalName ?? '',
    isAiVariable: !!externalProperty.aiVariableSettings,
  };
};

export const isPossibleForVariable = (externalProperty: ExternalProperty) => {
  return (
    externalProperty.valueType !== 'Boolean' &&
    externalProperty.valueType !== 'MultiSelect'
  );
};

export const getExternalPropertyForVariables = (
  externalProperties?: ExternalProperty[] | null,
): IVariable[] => {
  return (
    externalProperties
      ?.filter(
        (externalProperty) =>
          externalProperty.isTemplateVariable &&
          isPossibleForVariable(externalProperty),
      )
      .map((externalProperty) => {
        return createExternalPropertyVariable(externalProperty);
      }) ?? []
  );
};

export function cleanUrl(value: string): string {
  if (value.startsWith('https://')) {
    value = value.replace('https://', '');
  }
  if (value.startsWith('http://')) {
    value = value.replace('http://', '');
  }
  if (!value.startsWith('www.')) {
    value = `www.${value}`;
  }

  return value;
}

export function statusIsGreatherOrEqualThan(
  status: AccountStatus,
  toCompare: AccountStatus,
): boolean {
  const statusIndex = Object.values(AccountStatus).indexOf(status);
  const toCompareIndex = Object.values(AccountStatus).indexOf(toCompare);
  return statusIndex >= toCompareIndex;
}

export function cleanPixel(emailMessage: EmailMessage) {
  emailMessage.content = cleanPixelString(emailMessage.content);
  return emailMessage;
}

export function cleanPixelString(emailMessage = '') {
  if (!emailMessage) {
    return emailMessage;
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(emailMessage, 'text/html');

    const trackingPatterns = ['/email/track', '/email/attachment-1'];

    // Remove first tracking image found
    const images = doc.querySelectorAll('img');
    for (const img of images) {
      const src = img.getAttribute('src');
      const id = img.getAttribute('id');
      const hasSrcTracking =
        src &&
        trackingPatterns.some((pattern) =>
          src.toLowerCase().includes(pattern.toLowerCase()),
        );

      if (id === 'crn-trc' || hasSrcTracking) {
        img.remove();
        break;
      }
    }

    const bodyContent = doc.body ? doc.body.innerHTML : '';
    return bodyContent;
  } catch (error) {
    console.error('Error parsing email content with DOMParser:', error);
    return emailMessage;
  }
}

export function splitMessageContent(content: string) {
  const splitRegex =
    /(<div class=\\?"gmail_quote(?: gmail_quote_container)?\\?">)/g;

  const parts = content.split(splitRegex).filter((p) => p.length);

  let mainMessage = parts[0];

  let replies =
    parts.length > 1
      ? parts
          .slice(1)
          .reduce((acc, cur, i) => (i % 2 === 0 ? acc + cur : acc + cur), '')
      : '';

  // If mainMessage has no visible text, merge replies into it to avoid showing
  // an empty message body with all content hidden behind the collapsed dots
  if (replies && !mainMessage.replace(/<[^>]*>/g, '').trim()) {
    mainMessage = mainMessage + replies;
    replies = '';
  }

  return { mainMessage, replies };
}

export function calculatePercentage(
  n1: number | undefined,
  n2: number | undefined,
): number {
  if (!n1 || !n2) {
    return 0;
  }
  if (n1 === 0 || n2 === 0) {
    return 0;
  }

  return Math.round((n1 / n2) * 10000) / 100;
}

export function printUserRole(role: Role) {
  switch (role) {
    case Role.ADMIN:
      return 'Admin';
    case Role.MANAGER:
      return 'Manager';
    case Role.BASIC:
      return 'User';
    default:
      return 'User';
  }
}

export const disabled = (account: Account) => {
  if (!account) return true;
  return !account.owned;
};

export const getNextStepOrder = (tasks: TaskTodo[] | undefined | null) => {
  return tasks
    ? (tasks
        .filter((task) => !task.completed)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .at(0)?.order ?? 0)
    : 0;
};

export function countVariable(
  content: string,
  variable: TemplateVariables,
): [number, number] {
  let count = -1;
  let index = -1;
  let occurrenc = -1;
  do {
    index = occurrenc + 1;
    occurrenc = content.indexOf(variable, index);
    count++;
  } while (occurrenc !== -1);

  return [count, count * variable.length];
}

export function countOtherTemplateVariables(content: string): [number, number] {
  let count = 0;
  let index = -1;
  let totLen = 0;
  let occurrenc = -1;
  occurrenc = content.indexOf(TEMPLATE_VARIABLE_MISSING, index);
  while (occurrenc !== -1) {
    const end = content.indexOf('}}', occurrenc);
    if (end === -1) {
      index = occurrenc + 2;
      occurrenc = content.indexOf(TEMPLATE_VARIABLE_MISSING, index);
      continue;
    }
    const possibleTemplateVariable = content.substring(occurrenc + 2, end + 2);

    if (
      possibleTemplateVariable.includes('{{') ||
      possibleTemplateVariable.includes(' ')
    ) {
      index = occurrenc + 2;
      occurrenc = content.indexOf(TEMPLATE_VARIABLE_MISSING, index);
      continue;
    }
    totLen += end - occurrenc + 2;
    index = end + 2;
    count++;
    occurrenc = content.indexOf(TEMPLATE_VARIABLE_MISSING, index);
  }

  return [count, totLen];
}

export function countAllVariables(content: string): [number, number] {
  let count = 0;
  let varLength = 0;
  let totCount = 0;
  let totVar = 0;
  Object.values(TemplateVariables).forEach((variable) => {
    [count, varLength] = countVariable(content, variable);
    totCount += count;
    totVar += varLength;
  });
  [count, varLength] = countOtherTemplateVariables(content);
  totCount += count;
  totVar += varLength;

  return [totCount, totVar];
}

export const calculateTemplateLength = (
  content: string,
  creation: boolean,
): number => {
  if (creation) {
    content = content?.replaceAll('<p>&nbsp;</p>', ';');
  } else {
    content = content?.replaceAll('\n', ';');
  }
  const text = htmlToText(content || '');
  const [nOfVariables, lengthOfVariables] = countAllVariables(text ?? '');
  return (
    (text?.length || 1) -
    1 -
    lengthOfVariables +
    FIXED_VARIABLE_LENGTH * nOfVariables
  );
};

type StepSituation = 'NotRelevant' | 'ToDo' | 'Completed';

export interface IOnboardingSteps {
  SalesforceAdmin: StepSituation;
  CrmConnected: StepSituation;
  UsersInvited: StepSituation;
  LinkedinConnected: StepSituation;
}

export interface IOnboardingStepsWithSelectName extends IOnboardingSteps {
  hasToSelectName: boolean;
}

export const isOnboardingToDo = (
  user: User,
  onboardingIntegrations: IntegrationsOnboarding | null,
  subscription: SubscriptionStripe | null,
): IOnboardingSteps | null => {
  if (onboardingIntegrations) {
    const stepSituation: IOnboardingSteps = {
      SalesforceAdmin: 'NotRelevant',
      CrmConnected: 'NotRelevant',
      UsersInvited: 'NotRelevant',
      LinkedinConnected: 'NotRelevant',
    };
    let integrationToDo = false;
    const isSubscriptionCompleted = user.company?.subscriptionSetupCompleted;

    if (
      !isSubscriptionCompleted ||
      user.company?.integrationType !== IntegrationType.CRONO
    ) {
      stepSituation.CrmConnected = onboardingIntegrations.crmConnected
        ? 'Completed'
        : 'ToDo';
      integrationToDo ||= stepSituation.CrmConnected === 'ToDo';
    }

    if (!isSubscriptionCompleted) {
      if (subscription?.licenses === 1) {
        stepSituation.UsersInvited = 'NotRelevant';
      } else {
        stepSituation.UsersInvited = onboardingIntegrations.usersInvited
          ? 'Completed'
          : 'ToDo';
        integrationToDo ||= stepSituation.UsersInvited === 'ToDo';
      }
    }
    stepSituation.LinkedinConnected =
      onboardingIntegrations.linkedinConnected ||
      onboardingIntegrations.linkedinConnectedOnboarding
        ? 'Completed'
        : 'ToDo';
    integrationToDo ||= stepSituation.LinkedinConnected === 'ToDo';

    if (integrationToDo) return stepSituation;
    else {
      return null;
    }
  } else {
    return null;
  }
};

export const getColorsStages = (
  stageOrder: number,
  totalNumberOfStages: number,
  isWon = false,
  isLost = false,
) => {
  if (isWon) return wonAndLostColors[0];
  if (isLost) return wonAndLostColors[1];
  const numberOfColors = Object.keys(stagesColors).length - 2;

  let totalColors: StageColors[] = [];

  if (totalNumberOfStages <= numberOfColors) {
    totalColors = stagesColorsMap[totalNumberOfStages];
  } else {
    let currentTotal = totalNumberOfStages;
    while (currentTotal > 0) {
      totalColors = totalColors.concat(stagesColorsMap[numberOfColors]);
      currentTotal -= numberOfColors;
    }
  }

  return totalColors[stageOrder - 1] ?? stagesColorsMap[numberOfColors][0];
};

export interface OpportunityStageDisplay {
  name: string;
  color: string;
}

export const getOpportunityStageDisplay = (
  opportunity: Opportunity,
  pipelines: Pipeline[],
): OpportunityStageDisplay | null => {
  const pipeline = pipelines.find(
    (pipeline) => pipeline.externalName === opportunity.pipeline,
  );
  if (!pipeline) return null;

  const sortedStages = sortStagesByDisplayOrder(pipeline.stages);
  const stageIndex = sortedStages.findIndex(
    (stage) => stage.externalName === opportunity.stage,
  );
  const stage = sortedStages[stageIndex];
  if (!stage?.publicName) return null;

  const totalNumberOfStagesWithoutWonLost = sortedStages.filter(
    (stage) => !stage.isClosed,
  ).length;

  const colorsStage = getColorsStages(
    stageIndex + 1,
    totalNumberOfStagesWithoutWonLost,
    stage.isClosed && stage.isWon,
    stage.isClosed && !stage.isWon,
  );

  return { name: stage.publicName, color: colorsStage.dark };
};

export const getChartStageColors = (
  normalStages: number,
  lostStages: number,
  wonStages: number,
): string[] => {
  const numberOfColors = Object.keys(stagesColors).length - 2;
  const colors: string[] = [];

  let totalColors: StageColors[] = [];

  if (normalStages <= numberOfColors) {
    totalColors = stagesColorsMap[normalStages];
  } else {
    let currentTotal = normalStages;
    while (currentTotal > 0) {
      totalColors = totalColors.concat(stagesColorsMap[numberOfColors]);
      currentTotal -= numberOfColors;
    }
  }

  for (let i = 0; i < normalStages; i++) {
    colors.push(totalColors[i].dark);
  }

  for (let i = 0; i < wonStages; i++) {
    colors.push(stagesColors.green.dark);
  }

  for (let i = 0; i < lostStages; i++) {
    colors.push(stagesColors.red.dark);
  }

  return colors;
};

export const getTextOfTaskWarning = (warning: CronoTaskTodoWarning) => {
  switch (warning) {
    case CronoTaskTodoWarning.EmailMissing:
      return 'Missing/Invalid Email';
    case CronoTaskTodoWarning.PhoneMissing:
      return 'Missing/Invalid Phone';
    case CronoTaskTodoWarning.LinkedinMissing:
      return 'Missing/Invalid contact LinkedIn URL';
    case CronoTaskTodoWarning.TemplateVariableCanNotBeReplaced:
      return 'Missing template variable';
    case CronoTaskTodoWarning.PreviousTaskPending:
      return 'This task has been delayed due to a previous task still pending. No action needed';
    default:
      return '';
  }
};

export const getTextOfRewriteError = (error?: RewriteError | null) => {
  const defaultError = 'Rewrite went in error';
  if (!error) return defaultError;

  switch (error) {
    case RewriteError.ERROR_RELATED_TO_AI_MODEL:
      return 'Unexpected error related to the rewrite with AI model';
    case RewriteError.EXTENSION_NOT_AVAILABLE_DURING_INSERT:
      return 'Extension was not available during insert of the prospect to the sequence';
    case RewriteError.PROSPECT_LINKEDIN_URL_MISSING:
      return 'Prospect LinkedIn URL was missing during rewrite';
    case RewriteError.COMPANY_LINKEDIN_URL_MISSING:
      return 'Company LinkedIn URL was missing during rewrite';
    case RewriteError.UNHANDLED_ERROR_ON_EXTENSION:
      return 'Unexpected error on extension during rewrite';
    case RewriteError.UNHANDLED_ERROR:
      return 'Unexpected error during rewrite';
    default:
      return defaultError;
  }
};

export function getErrorCode(err: unknown): string | undefined {
  return (err as CronoError)?.response?.data?.errors?.[0]?.code;
}

export function getMfaErrorMessage(err: unknown): string {
  const code = getErrorCode(err);
  if (code === AuthErrorCode.MfaTempTokenExpired)
    return 'Your verification session has expired. Please log in again.';
  if (code === AuthErrorCode.MfaTooManyAttempts)
    return 'Too many failed attempts. Please log in again.';
  if (code === AuthErrorCode.MfaInvalidOtp)
    return 'Wrong code. Please try again.';
  return getError(err as CronoError) ?? 'Invalid verification code';
}

export function getTextOfAutomationError(
  error: CronoErrorCode | undefined,
  isInmail = false,
) {
  const defaultError =
    'An unexpected error occurred during the automatic submission';
  if (!error) return defaultError;

  switch (error) {
    case CronoErrorCode.AutomationFailedAccountNotActive:
      return 'The company is missing or deleted';
    case CronoErrorCode.AutomationFailedProspectNotActive:
      return 'The contact is missing or deleted';
    case CronoErrorCode.AutomationFailedProspectNotAssigned:
      return 'The contact is not assigned';
    case CronoErrorCode.AutomationFailedEmailSend:
      return 'Email automation failed';
    case CronoErrorCode.AutomationFailedAddressMissing:
      return 'Missing Email';
    case CronoErrorCode.AutomationFailedTemplateMissing:
      return 'Template is missing';
    case CronoErrorCode.AutomationFailedEmailCreation:
      return 'Email automation failed';
    case CronoErrorCode.AutomationFailedEmailSettingsMissing:
      return 'Email not properly connected';
    case CronoErrorCode.AutomationFailedEmailFormatNotValid:
      return 'Invalid Email';
    case CronoErrorCode.IntegrationNotValidError:
      return 'Email failed: email provider is not connected';
    case CronoErrorCode.AutomationFailedTemplateApplicationError:
      return 'Missing template variable';
    case CronoErrorCode.AutomationFailedUnsupportedTaskTypeError:
      return 'Task could not be automated';
    case CronoErrorCode.AutomationFailedLinkedinInvitationLimitReached:
      return 'LinkedIn weekly invitation limit reached on LinkedIn';
    case CronoErrorCode.AutomationFailedLinkedinSendMessage:
      if (isInmail) {
        return 'LinkedIn InMail message failed';
      } else {
        return 'LinkedIn message failed';
      }
    case CronoErrorCode.AutomationFailedLinkedinInMailCharsLimit:
      return 'LinkedIn InMail subject or message is too long';
    case CronoErrorCode.AutomationFailedLinkedinStatusUnknown:
      return 'Invalid contact LinkedIn URL';
    case CronoErrorCode.AutomationFailedLinkedinInvitationPending:
      return 'Invitation is still pending';
    case CronoErrorCode.AutomationFailedLinkedinInvitationMaxCharacters:
      return 'Invitation note is too long';
    case CronoErrorCode.AutomationFailedLinkedinUrlMissing:
      return 'Missing contact LinkedIn URL';
    case CronoErrorCode.AutomationFailedEmailNotValid:
      return 'Invalid Email';
    case CronoErrorCode.AutomationFailedLinkedinInvitationRecentlyWithdrawn:
      return 'This invitation failed as it was recently withdrawn';
    case CronoErrorCode.AutomationFailedSalesNavNotConnected:
      return 'Only Sales Navigator users can send InMail messages';
    case CronoErrorCode.AutomationFailedSalesNavNotLogged:
      return 'Be sure to be logged into Sales Navigator';
    case CronoErrorCode.AutomationFailedMessageToNotConnected:
      return 'LinkedIn message failed: recipient is not connected';
    case CronoErrorCode.ErrorAttachmentsLimitReached:
      return 'Attachment size is too big';
    case CronoErrorCode.AutomationFailedLinkedinUrlInvalid:
      return 'Invalid contact LinkedIn URL';
    case CronoErrorCode.MailBoxNotPresent:
      return 'Selected mailbox is disconnected or no longer available';
    case CronoErrorCode.NoMailBoxConnected:
      return "No mailbox connected. Please connect your mailbox in the 'Settings' page";
    case CronoErrorCode.TaskErrorEmailBounced:
      return 'Email bounced';
    case CronoErrorCode.AutomationFailedThreadNotFound:
      return 'Email thread could not be retrieved';
    case CronoErrorCode.OutlookThrottled:
      return 'Email failed: Outlook is limiting requests, please try again later';
    case CronoErrorCode.OutlookRequestTimeout:
      return 'Email failed: Outlook did not respond in time, please try again later';
    case CronoErrorCode.OutlookSendOutcomeUnknown:
      return 'Email may have been sent: check your Outlook Sent Items before retrying';
    case CronoErrorCode.OutlookTokenExpired:
      return 'Email failed: Outlook connection expired, please reconnect your mailbox';
    case CronoErrorCode.OutlookApiFailed:
      return 'Email failed: Outlook request failed';
    case CronoErrorCode.ProspectOptedOut:
      return 'The action was not performed as the user opted out';
    case CronoErrorCode.MailBoxDisconnected:
      return 'Selected mailbox is disconnected';
    case CronoErrorCode.AutomationFailedMaxSendEmailLimit:
      return "Email failed due to your provider's daily limit. Sending will be tried again tomorrow";
    case CronoErrorCode.DailySendLimitReached:
      return 'Daily limit reached, tasks can be executed on next working day';
    default:
      return defaultError;
  }
}

export const sortPlans = (plans: Options[]): Options[] => {
  const result: Options[] = [];
  const freeTrial = plans.find(
    (plan) => plan.subscriptionType === SubscriptionPlanType.FREE_TRIAL,
  );
  if (freeTrial) result.push(freeTrial);
  const starter = plans.find(
    (plan) => plan.subscriptionType === SubscriptionPlanType.STARTER,
  );
  if (starter) result.push(starter);
  const pro = plans.find(
    (plan) => plan.subscriptionType === SubscriptionPlanType.PRO,
  );
  if (pro) result.push(pro);
  const ultra = plans.find(
    (plan) => plan.subscriptionType === SubscriptionPlanType.ULTRA,
  );
  if (ultra) result.push(ultra);
  return result;
};

export const useGetStatusesOptions = (
  type: StatusComponentOptions | 'lead',
  enabled = true,
) => {
  const tableType: TableType =
    type === 'account' ? 'Account' : type === 'lead' ? 'Lead' : 'Prospect';
  const { data: externalPropertyStatus } = useGetExternalPropertyStatus(
    tableType,
    enabled,
  );

  const externalPropertyStatusId = externalPropertyStatus?.data?.data?.id;

  const isExternalPropertyStatus = !!externalPropertyStatus?.data?.data;
  const statuses: ExternalPropertyShort[] = useMemo(() => {
    return !isExternalPropertyStatus
      ? type === 'account'
        ? DefaultCompanyStatus
        : DefaultContactStatus
      : externalPropertyStatus.data?.data?.options
        ? externalPropertyStatus.data.data.options.map((option, index) => {
            return {
              id: index,
              option: option,
              label:
                getExternalPropertyLabels(
                  externalPropertyStatus.data!.data!,
                ).at(index) ?? option,
            };
          })
        : [];
  }, [isExternalPropertyStatus, type, externalPropertyStatus]);

  const externalPropertyDefaultStatus =
    externalPropertyStatus?.data?.data?.defaultValue ??
    (type === 'account'
      ? DefaultDefaultCompanyStatus.option
      : DefaultDefaultContactStatus.option);

  return {
    statuses,
    externalPropertyStatusId,
    isExternalPropertyStatus,
    externalPropertyDefaultStatus,
  };
};

export const IndexFirstLinkedinChat = (
  steps: StrategyStep[] | TaskTodo[] | null | undefined,
) => {
  if (!steps) return null;
  for (let i = 0; i < steps.length; i++) {
    const subtype = steps[i].subtype;
    if (
      steps[i].type === TaskTodoType.LINKEDIN &&
      (subtype === TaskTodoSubtype.LinkedinMessage ||
        subtype === TaskTodoSubtype.LinkedinVoiceNote ||
        subtype === TaskTodoSubtype.LinkedinInvitation)
    )
      return i;
  }
  return null;
};

export const IndexFirstLinkedinChatAfterInvitation = (
  steps: StrategyStep[] | TaskTodo[] | null | undefined,
) => {
  if (!steps) return -1;
  let firstInvitationFound = false;

  for (let i = 0; i < steps.length; i++) {
    const subtype = steps[i].subtype;
    if (
      steps[i].type === TaskTodoType.LINKEDIN &&
      (subtype === TaskTodoSubtype.LinkedinMessage ||
        subtype === TaskTodoSubtype.LinkedinVoiceNote ||
        subtype === TaskTodoSubtype.LinkedinInvitation) &&
      firstInvitationFound
    ) {
      return i;
    }
    if (
      steps[i].type === TaskTodoType.LINKEDIN &&
      steps[i].subtype === TaskTodoSubtype.LinkedinInvitation
    ) {
      firstInvitationFound = true;
    }
  }
  return -1;
};

export interface StrategyDurationRange {
  min: number;
  max: number;
}

// Mirrors the date preview in strategyDetailModal (calculatedDates): the main
// path waits up to linkedinUntilAcceptedDays for the invitation at the first
// conditional step, the conditional path then follows daysAfterConditionNotAccepted.
export const getStrategyDuration = (
  steps: StrategyStep[] | null | undefined,
): StrategyDurationRange | null => {
  if (!steps?.length) return null;

  const indexFirstLinkedinChat = IndexFirstLinkedinChat(steps);
  let delaysSum = 0;
  let totalDelay = 0;
  let delayBetweenInvitationAndConditional = 0;
  let conditionalFlowTotalDelay = 0;
  let firstConditional = true;

  steps.forEach((step, index) => {
    delaysSum += step.delay;

    if (!step.strategyStepConditional) {
      totalDelay += step.delay;
      conditionalFlowTotalDelay += step.delay;
      if (indexFirstLinkedinChat !== null && index > indexFirstLinkedinChat) {
        delayBetweenInvitationAndConditional += step.delay;
      }
      return;
    }

    if (firstConditional) {
      const { linkedinUntilAcceptedDays } = step.strategyStepConditional;
      if (delayBetweenInvitationAndConditional < linkedinUntilAcceptedDays) {
        totalDelay +=
          linkedinUntilAcceptedDays - delayBetweenInvitationAndConditional;
      }
      conditionalFlowTotalDelay = totalDelay;
      firstConditional = false;
    }

    totalDelay += step.delay;
    conditionalFlowTotalDelay +=
      step.strategyStepConditional.daysAfterConditionNotAccepted;
  });

  return {
    min: Math.min(delaysSum, conditionalFlowTotalDelay),
    max: Math.max(totalDelay, conditionalFlowTotalDelay),
  };
};

export const formatStrategyDuration = (
  { min, max }: StrategyDurationRange,
  separator = ' - ',
) =>
  min === max
    ? pluralize(min, 'day', 'days')
    : `${min}${separator}${pluralize(max, 'day', 'days')}`;

export const formatStrategyDurationLabel = (range: StrategyDurationRange) => {
  if (range.max === 0) return 'Same day';
  if (range.min === 0) return `Up to ${pluralize(range.max, 'day', 'days')}`;
  return formatStrategyDuration(range);
};

export const formatStrategyDurationTooltip = (range: StrategyDurationRange) => {
  if (range.max === 0) return 'Sequence completes on the same day';
  if (range.min === 0)
    return `Sequence duration is up to ${pluralize(range.max, 'day', 'days')}`;
  return `Sequence duration is ${formatStrategyDuration(range, '-')}`;
};

export const stopAndJumpConditionalPresent = (
  steps: StrategyStep[] | TaskTodo[] | null | undefined,
) => {
  return !!steps?.some(
    (step) =>
      step.strategyStepConditional?.type ===
      SwitchToTask.STOP_AND_JUMP_TO_ANOTHER_STRATEGY,
  );
};

export const parseTaskTypeToString = (
  type: TaskTodoType,
  subtype?: TaskTodoSubtype | null,
  forConditionals?: boolean,
) => {
  switch (type) {
    case TaskTodoType.CALL:
      return 'Call';
    case TaskTodoType.EMAIL:
      return 'Email';
    case TaskTodoType.LINKEDIN:
      if (subtype === TaskTodoSubtype.LinkedinInvitation) {
        return forConditionals ? 'Invitation' : 'Linkedin Invitation';
      } else if (subtype === TaskTodoSubtype.LinkedinVoiceNote) {
        return forConditionals ? 'Voice Note' : 'Linkedin Voice Note';
      } else if (subtype === TaskTodoSubtype.LinkedinProfileView) {
        return forConditionals ? 'Profile View' : 'Linkedin Profile View';
      } else if (subtype === TaskTodoSubtype.LinkedinLikeLastPost) {
        return forConditionals ? 'Like Reaction' : 'Linkedin Like Reaction';
      } else {
        return forConditionals ? 'Message' : 'Linkedin Message';
      }
    case TaskTodoType.IN_MAIL:
      return forConditionals ? 'InMail' : 'Linkedin InMail';
    case TaskTodoType.GENERIC:
      return 'To-do';
  }
};

export const parseConditionalTypeToString = (type?: SwitchToTask) => {
  switch (type) {
    case SwitchToTask.TO_EMAIL:
      return 'Email';
    case SwitchToTask.IN_MAIL:
      return 'InMail';
    default:
      return '';
  }
};

export function mapSubscriptionToPlanType(subscription: string | undefined) {
  switch (subscription) {
    case SubscriptionPlanType.FREE_TRIAL:
      return Plan.FREE_TRIAL;
    case SubscriptionPlanType.STARTER:
      return Plan.STARTER;
    case SubscriptionPlanType.PRO:
      return Plan.PRO;
    case SubscriptionPlanType.ULTRA:
      return Plan.ULTRA;
    default:
      return null;
  }
}

export function mapPlanToSubscriptionType(plan: Plan | undefined) {
  switch (plan) {
    case Plan.FREE_TRIAL:
      return SubscriptionPlanType.FREE_TRIAL;
    case Plan.STARTER:
      return SubscriptionPlanType.STARTER;
    case Plan.PRO:
      return SubscriptionPlanType.PRO;
    case Plan.ULTRA:
      return SubscriptionPlanType.ULTRA;
    default:
      return null;
  }
}

export const mapBillingPlanToText = (option: BillingPlan) => {
  switch (option) {
    case 'monthly':
      return 'monthly';
    case 'quarterly':
      return 'quarterly';
    case 'yearly':
      return 'annually';
    default:
      return '';
  }
};

export function mapTaskTypeToTemplateType(
  type: TaskTodoType,
  subtype: TaskTodoSubtype | undefined | null,
): TemplateType | null {
  switch (type) {
    case TaskTodoType.CALL:
      return TemplateType.SCRIPT;
    case TaskTodoType.EMAIL:
      return TemplateType.EMAIL;
    case TaskTodoType.IN_MAIL:
      return TemplateType.IN_MAIL;
    case TaskTodoType.LINKEDIN:
      switch (subtype) {
        case TaskTodoSubtype.LinkedinMessage:
          return TemplateType.LINKEDIN;
        case TaskTodoSubtype.LinkedinInvitation:
          return TemplateType.INVITATION;
        case TaskTodoSubtype.LinkedinVoiceNote:
          return TemplateType.VOICE_NOTE;
        default:
          return TemplateType.LINKEDIN;
      }
    default:
      return null;
  }
}

export const getSenderName = (
  mailboxSenderName: string | null,
  user: User | null,
) => {
  if (mailboxSenderName) {
    return mailboxSenderName;
  }
  return user ? getFullName(user) || '' : '';
};

// The backend can keep `connected` true after the token expires or is revoked,
// flagging the mailbox only through `disconnectReason`.
export const isMailboxConnected = (mailbox: Mailbox) =>
  mailbox.connected && !mailbox.disconnectReason;

export const createErrorIfExtensionIsNotFullyFunctional = (
  extensionStatus: CronoExtensionStatus | undefined,
) => {
  if (extensionStatus !== CronoExtensionStatus.INSTALLED_WITH_LINKEDIN_COOKIE) {
    return Promise.reject({
      response: {
        data: {
          data: null,
          isSuccess: false,
          errors: [
            {
              message:
                'You need to have working extension to perform this action',
              code: 'ExtensionNotInstalled',
            },
          ],
          status: 400,
        },
        status: 400,
        statusText: 'Bad Request',
        headers: {},
        config: {},
      } as Response<null>,
    });
  }
};

export const handleOpenLinkedinTabWithCronoExtensionOpened = (
  salesNav = false,
) => {
  const queryParams = Constants.QUERY_PARAM_OPEN_POPUP;
  const value = Constants.QUERY_PARAM_OPEN_POPUP_VALUE;
  const newQueryParams = new URLSearchParams({
    [queryParams]: value,
  }).toString();
  const url = `${salesNav ? `${Constants.salesNavBaseUrl}/home/` : `${Constants.linkedinBaseUrl}/feed/`}?${newQueryParams}`;
  window.open(url, '_blank', 'noopener,noreferrer');
};

export const getStripeEnvironmentToUse = (selectedCountry: string | null) => {
  if (selectedCountry === 'IT') {
    return 'It';
  }
  return 'Uk';
};

type ParseResult = [TaskTodoType, TaskTodoSubtype | null];
export const parseConversationType = (
  conversationType: ConversationType,
): ParseResult => {
  switch (conversationType) {
    case 'Email':
      return [TaskTodoType.EMAIL, null];
    case 'LinkedinInvitation':
      return [TaskTodoType.LINKEDIN, TaskTodoSubtype.LinkedinInvitation];
    case 'LinkedinMessage':
      return [TaskTodoType.LINKEDIN, TaskTodoSubtype.LinkedinMessage];
    case 'LinkedinVoiceNote':
      return [TaskTodoType.LINKEDIN, TaskTodoSubtype.LinkedinVoiceNote];
    case 'InMail':
      return [TaskTodoType.IN_MAIL, null];
    case 'LinkedinProfileView':
      return [TaskTodoType.LINKEDIN, TaskTodoSubtype.LinkedinProfileView];
    case 'LinkedinLikeLastPost':
      return [TaskTodoType.LINKEDIN, TaskTodoSubtype.LinkedinLikeLastPost];
    case 'Call':
      return [TaskTodoType.CALL, null];
    default:
      throw new RangeError(`Unexpected conversationType: ${conversationType}`);
  }
};

export function getTextSimilarity(a: string, b: string): number {
  const textA = htmlToText(a || '').trim();
  const textB = htmlToText(b || '').trim();

  if (!textA && !textB) return 1;
  if (!textA || !textB) return 0;

  function levenshtein(s: string, t: string): number {
    const m = s.length;
    const n = t.length;
    const d: number[][] = [];
    for (let i = 0; i <= m; i++) {
      d[i] = [i];
    }
    for (let j = 1; j <= n; j++) {
      d[0][j] = j;
    }
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        const cost = s[i - 1] === t[j - 1] ? 0 : 1;
        d[i][j] = Math.min(
          d[i - 1][j] + 1,
          d[i][j - 1] + 1,
          d[i - 1][j - 1] + cost,
        );
      }
    }
    return d[m][n];
  }

  const distance = levenshtein(textA, textB);
  const maxLen = Math.max(textA.length, textB.length);
  if (maxLen === 0) return 1;
  return 1 - distance / maxLen;
}

export function getLocaleCountryCode(defaultCode = 'US'): string {
  try {
    if (
      'Intl' in window &&
      'Locale' in Intl &&
      typeof navigator.language === 'string'
    ) {
      const locale = new Intl.Locale(navigator.language);

      if (locale.region) {
        return locale.region;
      }

      // Map language-only codes to their primary country
      const languageToCountryMap: Record<string, string> = {
        it: 'IT',
        fr: 'FR',
        de: 'DE',
        es: 'ES',
        pt: 'PT',
        nl: 'NL',
        pl: 'PL',
        ru: 'RU',
        ja: 'JP',
        ko: 'KR',
        zh: 'CN',
        ar: 'SA',
        sv: 'SE',
        da: 'DK',
        no: 'NO',
        fi: 'FI',
        el: 'GR',
        tr: 'TR',
        he: 'IL',
        th: 'TH',
        vi: 'VN',
        hi: 'IN',
        id: 'ID',
        ms: 'MY',
        tl: 'PH',
        uk: 'UA',
        cs: 'CZ',
        sk: 'SK',
        hu: 'HU',
        ro: 'RO',
        bg: 'BG',
        hr: 'HR',
        sl: 'SI',
        et: 'EE',
        lv: 'LV',
        lt: 'LT',
        mt: 'MT',
        is: 'IS',
      };

      return languageToCountryMap[locale.language] || defaultCode;
    }

    const parts = navigator.language?.split('-');
    if (parts?.length === 2) {
      return parts[1].toUpperCase();
    }

    return defaultCode;
  } catch (err) {
    console.warn('Could not determine locale country code:', err);
    return defaultCode;
  }
}

export function getEnrichError(error: CronoErrorCode, tableType: TableType) {
  switch (error) {
    case CronoErrorCode.GenerateAiVariableEntityNotFound:
      return 'Error while obtaining information regarding the current entity';
    case CronoErrorCode.GenerateAiVariableVariableNotFound:
      return `Missing variables. Check if variables used in the prompt are present for the ${tableType === 'Account' ? 'company' : tableType === 'Prospect' ? 'contact' : 'lead'}`;
    case CronoErrorCode.GenerateAiVariablePromptError:
      return 'Error while retrieving or generating the prompt for the AI model';
    case CronoErrorCode.GenerateAiVariableVariableMissingError:
      return 'Some of the variables are missing. Check if they are still present in the system';
    case CronoErrorCode.GenerateAiVariableVariableEmptyPromptError:
      return 'Some of the variables are empty. Populate it to proceed';
    case CronoErrorCode.GenerateAiVariableInternalError:
      return 'Internal error while generating the value';
    case CronoErrorCode.AssistantRequestsFinished:
      return 'Not enough credit to complete the request';
    default:
      return 'An error occurred while generating the value';
  }
}

export const getMailboxSignatureBlockIfPresent = (
  mailbox: Mailbox | undefined | null,
) => {
  if (!mailbox || !mailbox.signature) {
    return '';
  }

  const { signatureDataAttribute, htmlBlotTagName } = FeConstants;

  // Like Gmail: the line to type on, one blank line, then, when the mailbox
  // opts in, the RFC 3676 "-- " delimiter, which lets the recipient's client
  // collapse the signature.
  const signatureSeparator = mailbox.showSignatureDelimiter
    ? '<br/><br/>-- <br/>'
    : '<br/><br/>';

  const isHtmlBlot = mailbox.signature.includes(`<${htmlBlotTagName}>`);

  if (isHtmlBlot) {
    return (
      signatureSeparator +
      mailbox.signature.replace(
        `<${htmlBlotTagName}>`,
        `<${htmlBlotTagName} ${signatureDataAttribute}="true">`,
      )
    );
  } else {
    return (
      signatureSeparator +
      `<${htmlBlotTagName} ${signatureDataAttribute}="true">` +
      mailbox.signature +
      `</${htmlBlotTagName}>`
    );
  }
};

const isBreak = (node: Node | null): node is Element => node?.nodeName === 'BR';

// Drafts saved before the separator shrank still carry three <br/> ahead of
// the "-- " delimiter; trim them to the two getMailboxSignatureBlockIfPresent
// now emits.
const normalizeSignatureDelimiterBreaks = (signature: Element) => {
  const lineBreak = signature.previousSibling;
  const delimiter = isBreak(lineBreak) ? lineBreak.previousSibling : null;
  if (
    delimiter?.nodeType !== Node.TEXT_NODE ||
    delimiter.textContent?.trim() !== '--'
  ) {
    return;
  }

  const breaks: Element[] = [];
  let node = delimiter.previousSibling;
  while (isBreak(node)) {
    breaks.push(node);
    node = node.previousSibling;
  }
  breaks.slice(2).forEach((br) => br.remove());
};

const isSignatureDelimiter = (node: Node | null) =>
  node?.textContent?.trim() === '--';

// The "-- " line ahead of the signature comes in two shapes: the text node and
// <br> getMailboxSignatureBlockIfPresent emits, or a paragraph of its own once
// the editor has normalised the body.
const getSignatureDelimiterNodes = (signature: Element): Node[] => {
  const lineBreak = signature.previousSibling;
  if (isBreak(lineBreak)) {
    const text = lineBreak.previousSibling;
    if (text?.nodeType === Node.TEXT_NODE && isSignatureDelimiter(text)) {
      return [text, lineBreak];
    }
  }

  const paragraph = signature.previousElementSibling;
  if (paragraph && !isBreak(paragraph) && isSignatureDelimiter(paragraph)) {
    return [paragraph];
  }
  return [];
};

/**
 * Replaces the signature already in `content` with the one of `mailbox`, so
 * switching mailbox never leaves two signatures in the body. When `content`
 * has no signature, the new one is appended only if `appendIfMissing` is set.
 */
export const replaceMailboxSignatureBlock = (
  content: string,
  mailbox: Mailbox | undefined | null,
  appendIfMissing: boolean,
) => {
  const signatureSelector = `[${FeConstants.signatureDataAttribute}]`;
  const parser = new DOMParser();
  const contentDocument = parser.parseFromString(content, 'text/html');
  const currentSignature =
    contentDocument.body.querySelector(signatureSelector);
  const newSignatureBlock = getMailboxSignatureBlockIfPresent(mailbox);

  if (!currentSignature) {
    return appendIfMissing ? content + newSignatureBlock : content;
  }

  const newSignatureDocument = parser.parseFromString(
    newSignatureBlock,
    'text/html',
  );
  const newSignature =
    newSignatureDocument.body.querySelector(signatureSelector);

  normalizeSignatureDelimiterBreaks(currentSignature);
  const delimiterNodes = getSignatureDelimiterNodes(currentSignature);

  if (newSignature && mailbox?.showSignatureDelimiter) {
    if (delimiterNodes.length === 0) {
      currentSignature.before(
        contentDocument.createTextNode('-- '),
        contentDocument.createElement('br'),
      );
    }
  } else {
    delimiterNodes.forEach((node) => node.parentNode?.removeChild(node));
  }

  if (newSignature) {
    currentSignature.replaceWith(newSignature);
  } else {
    currentSignature.remove();
  }

  return contentDocument.body.innerHTML;
};

export function printCallSentiment(callSentiment: CallSentiment | undefined) {
  switch (callSentiment) {
    case CallSentiment.CALL_BACK:
      return 'Call back';
    case CallSentiment.MEETING_SCHEDULED:
      return 'Meeting scheduled';
    case CallSentiment.NOT_INTERESTED:
      return 'Not interested';
    case CallSentiment.NOT_RIGHT_CONTACT:
      return 'Not right contact';
    case CallSentiment.SEND_EMAIL:
      return 'Send email';
    case CallSentiment.PERSONAL_NUMBER:
      return 'Personal number';
    default:
      return '';
  }
}

export function printCallFeedback(callFeedback: CallFeedback) {
  switch (callFeedback) {
    case CallFeedback.CONNECTED:
      return 'Connected';
    case CallFeedback.NOT_ANSWERED:
      return 'Not Answered';
    case CallFeedback.GATE_KEEPER:
      return 'Gatekeeper';
    case CallFeedback.VOICEMAIL:
      return 'Voicemail';
    case CallFeedback.WRONG_NUMBER:
      return 'Wrong Number';
    case CallFeedback.BUSY:
      return 'Busy';
    default:
      return '';
  }
}

export function checkIsProspectLead(
  user: User | null | undefined,
  prospect:
    | Prospect
    | ProspectSearchStrategyResponse
    | ProspectLinkedin
    | null
    | undefined,
) {
  if (!user || !prospect) {
    return false;
  }

  return (
    doesCrmSupportLeads(user?.company?.integrationType) &&
    !prospect?.fromContact
  );
}

type Only<T, U> = {
  [P in keyof T]: T[P];
} & {
  [P in keyof U]?: never;
};

export type Either<T, U> = Only<T, U> | Only<U, T>;

export type Either3<T, U, V> =
  | Only<T, U>
  | Only<U, T>
  | Only<T, V>
  | Only<V, T>
  | Only<U, V>
  | Only<V, U>;
